import { countOccupantsForProperty, occupantsExceedIncludedGuests, occupantsFitRoomCapacity } from 'lib/offer/occupancyUtils'

export function isReservationEmpty(reservation: App.Occupants) {
  return reservation.adults === 0 &&
    !reservation.childrenAge?.length &&
    reservation.teenagers === 0 &&
    reservation.children === 0 &&
    reservation.infants === 0
}

export function extractOccupantsFromReservation(reservation?: App.Occupants) {
  if (!reservation || isReservationEmpty(reservation)) {
    return []
  }
  return [{
    adults: reservation.adults,
    teenagers: reservation.teenagers,
    children: reservation.children,
    infants: reservation.infants,
    childrenAge: reservation.childrenAge,
  }]
}

export function allRoomsOccupantsFitRoomCapacity(
  occupancy: App.Occupants,
  capacities: Array<App.RoomCapacity>,
  offerOrProperty: App.Offer | App.OfferSummary | App.BedbankOffer | App.BundledOffer | App.OfferProperty,
) {
  const isOffer = 'type' in offerOrProperty
  const property = isOffer
    ? (offerOrProperty.type !== 'bedbank_hotel' ? offerOrProperty.property : undefined)
    : offerOrProperty
  if (property?.useDynamicOccupancy && !capacities.length) {
    return true
  }

  return capacities.some(capacity => occupantsFitRoomCapacity(occupancy, capacity, offerOrProperty))
}

export function hasIncludedGuestsExceeded(
  occupancy: App.Occupants,
  pkg: App.Package,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
) {
  if (pkg.roomRate?.includedGuests.length) {
    const transformedOccupants = countOccupantsForProperty([occupancy], offer.property)
    return occupantsExceedIncludedGuests(transformedOccupants, pkg.roomRate.includedGuests)
  }

  return false
}

export function includedGuestsExceededItems(
  guests: Array<App.Occupants>,
  pkg: App.Package,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
) {
  return guests.some(guests => guests.adults >= 1 &&
    hasIncludedGuestsExceeded(guests, pkg, offer))
}

export function getExtraGuests(
  occupancy: App.Occupants,
  pkg: App.Package,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
) {
  if (hasIncludedGuestsExceeded(occupancy, pkg, offer)) {
    const transformedOccupants = countOccupantsForProperty([occupancy], offer.property)
    return pkg.roomRate?.includedGuests.map(included => ({
      adults: Math.max(transformedOccupants.adults - included.adults, 0),
      teenagers: Math.max(transformedOccupants.teenagers - included.teenagers, 0),
      children: Math.max(transformedOccupants.children - included.children, 0),
      infants: Math.max(transformedOccupants.infants - included.infants, 0),
    }))
  }

  return []
}

export interface ExtraGuestSurchargeSplit {
  total: number
  /** The part of `total` the hotel collects rather than us. */
  payableAtProperty: number
}

/**
 * A room's extra guest surcharge, split by who collects it.
 *
 * The seating is chosen once, on the combined total, and only then split. Choosing it
 * separately per destination would let the same guest land in a different slot on each
 * bill, so the two would not add up to what was quoted.
 */
export const getRoomExtraGuestSurchargeSplit = (
  pkg: App.Package,
  occupancy: App.Occupants,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
): ExtraGuestSurchargeSplit => {
  const extraGuests = getExtraGuests(occupancy, pkg, offer)
  const surcharges = pkg.roomRate?.extraGuestSurcharges

  if (surcharges?.length && extraGuests && extraGuests.length > 0) {
    const amountOf = (rows: typeof surcharges, extraGuest: typeof extraGuests[number]) => rows.reduce((acc, surcharge) => {
      const adults = surcharge.adultAmount * extraGuest.adults
      const teenagers = surcharge.teenagerAmount * extraGuest.teenagers
      const children = surcharge.childAmount * extraGuest.children
      const infants = surcharge.infantAmount * extraGuest.infants
      return acc + adults + teenagers + children + infants
    }, 0)

    const seatings = extraGuests.reduce((acc, extraGuest) => {
      const total = amountOf(surcharges, extraGuest)
      if (total > 0) {
        acc.push({ total, payableAtProperty: amountOf(surcharges.filter(surcharge => surcharge.payableAtProperty), extraGuest) })
      }
      return acc
    }, [] as Array<ExtraGuestSurchargeSplit>)

    if (!seatings.length) {
      return { total: 0, payableAtProperty: 0 }
    }

    const cheapest = seatings.reduce((min, seating) => seating.total < min.total ? seating : min)
    return {
      total: Math.floor(cheapest.total * pkg.duration),
      payableAtProperty: Math.floor(cheapest.payableAtProperty * pkg.duration),
    }
  }

  return { total: 0, payableAtProperty: 0 }
}

export const getRoomExtraGuestSurcharge = (
  pkg: App.Package,
  occupancy: App.Occupants,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
) => getRoomExtraGuestSurchargeSplit(pkg, occupancy, offer).total

export const getSumExtraGuestsSurchargeSplit = (
  occupancies: Array<App.Occupants>,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
  pkg?: App.Package,
): ExtraGuestSurchargeSplit => {
  if (pkg?.roomRate?.extraGuestSurcharges.length && occupancies?.length > 0) {
    return occupancies.reduce((acc, occupancy) => {
      const room = getRoomExtraGuestSurchargeSplit(pkg, occupancy, offer)
      return { total: acc.total + room.total, payableAtProperty: acc.payableAtProperty + room.payableAtProperty }
    }, { total: 0, payableAtProperty: 0 })
  }

  return { total: 0, payableAtProperty: 0 }
}

export const getSumExtraGuestsSurcharge = (
  occupancies: Array<App.Occupants>,
  offer: App.Offer | App.OfferSummary | App.BundledOffer,
  pkg?: App.Package,
) => getSumExtraGuestsSurchargeSplit(occupancies, offer, pkg).total
